import {
    getIsSocialAppEnabled,
    getIsSocialEnabled,
} from '@socialFederation/helpers';
import { logger } from '@src/app/logger';
import {
    ShellRemoteConfig,
    ShellRemoteConfigDto,
    WinTaxBound,
} from './remoteConfigTypes';
import {
    parseRemoteSuperBonusValidBetTypes,
    parseSuperAdvantageSports,
} from './remoteConfigUtils';
import { parseSuperBonusBreakpoints } from '@superbet-group/betting.lib.payments';
import { ObservabilitySDKConfig } from '@superbet-group/web.observability.sdk-bootstrap';

export function getShellRemoteConfig(remoteConfigDto: ShellRemoteConfigDto): ShellRemoteConfig {
    return {
        sportsOffer: {
            host: `${remoteConfigDto.offerServer}/v2`,
            sseHost: `${remoteConfigDto.offerServer}/subscription/v2`,
            useSimplifiedTournamentName: !!remoteConfigDto.useSimplifiedTournamentName,
            forceTimeFilter: !!remoteConfigDto.forceTimeFilter,
        },
        superBonus: {
            isEnabled: remoteConfigDto.superBonusEnabled === 'true' || remoteConfigDto.superBonusEnabled === true,
            isDoubleGenerosityDescriptionVisible: !!remoteConfigDto.isDoubleGenerosityDescriptionVisible,
            validBetTypes: parseRemoteSuperBonusValidBetTypes(remoteConfigDto.superBonusValidBetTypes),
            breakpoints: remoteConfigDto.superBonusBreakpoints
                ? parseSuperBonusBreakpoints(remoteConfigDto.superBonusBreakpoints)
                : [],
            superBonusMinCoef: remoteConfigDto.superBonusMinCoef || '',
            superBonusMinSelections: remoteConfigDto.superBonusMinSelections || '',
        },
        betSlip: {
            winTaxBounds: parseWinTaxBounds(remoteConfigDto.winTax),
        },
        popularSuperbetsV2: remoteConfigDto.popularSuperbetsV2,
        areSitesSplitByLicence: remoteConfigDto.areSitesSplitByLicence || false,
        generosity: {
            superAdvantagePagesSports: parseSuperAdvantageSports(remoteConfigDto.superAdvantagePagesSports),
            homePagePriceBoostPosition: remoteConfigDto.homePagePriceBoostPosition,
        },
        wallet: {
            deposit: {
                isFtdReminderEnabled: !!remoteConfigDto.isFtdReminderEnabled,
            },
        },
        social: {
            isSocialEnabled: getIsSocialEnabled(remoteConfigDto),
            isSocialAppEnabled: getIsSocialAppEnabled(remoteConfigDto),
        },
        wiki: {
            wikiRawHtmlIFrameHostWhitelist: remoteConfigDto.wikiRawHtmlIFrameHostWhitelist,
        },
        geoLocationCheckPeriodMinutes: remoteConfigDto.geoLocationCheckPeriodMinutes || 30,
        // on timeout we block user action behind the geoLocation check
        geoLocationPingTimeoutMs: remoteConfigDto.geoLocationPingTimeoutMs || 5000,
        isProdGeoLocation: !!remoteConfigDto.isProdGeoLocation,
        checkLoginSessionOnNavigation: !!remoteConfigDto.checkLoginSessionOnNavigation,
        observabilitySdk: {
            configs: ObservabilitySDKConfig.parseStringToListOfUrls(remoteConfigDto.observabilitySdkConfigs || ''),
        },
        locationPermissionFaqIosUrl: remoteConfigDto.locationPermissionFaqIosUrl || '/',
        locationPermissionFaqAndroidUrl: remoteConfigDto.locationPermissionFaqAndroidUrl || '/',
        locationPermissionFaqUrl: remoteConfigDto.locationPermissionFaqUrl || '/',
        sportBreadcrumbIdFromStruct: remoteConfigDto.sportBreadcrumbIdFromStruct || false,
        experiment: {
            apiUrl: remoteConfigDto.experimentApiUrl || null,
        },
        homepageSurveySetup: remoteConfigDto.homepageSurveySetup,
        profilePageSurveySetup: remoteConfigDto.profilePageSurveySetup,
        surveyBaseSetup: remoteConfigDto.surveyBaseSetup,
        experiments: {
            minimalRegistrationDateForRedirectAfterLogin: parseDateWithoutTime('minimalRegistrationDateForRedirectAfterLoginExperiment', remoteConfigDto.minimalRegistrationDateForRedirectAfterLoginExperiment),
            minimalRegistrationDateForDepositFormInHome: parseDateWithoutTime('minimalRegistrationDateForDepositFormInHomeExperiment', remoteConfigDto.minimalRegistrationDateForDepositFormInHomeExperiment),
            idOfGamesSectionInSportsHome: remoteConfigDto.idOfGamesSectionInSportsHome,
            onboardingChallengeExperimentStartDate: parseDateWithoutTime('onboardingChallengeExperimentStartDate', remoteConfigDto.onboardingChallengeExperimentStartDate),
        },
        isMultiLoginTarget: remoteConfigDto.isMultiLoginTarget || false,
        prerenderReadyDelay: remoteConfigDto.prerenderReadyDelay || 0,
    };
}

const defaultTaxLevel = '0|1';

function parseDateWithoutTime(whichDate: string, dateString: string | undefined): Date | undefined {
    if (!dateString) {
        logger.error(new Error(`${whichDate} is not defined`));
        return undefined;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        logger.error(new Error(`${whichDate} has invalid format`));
        return undefined;
    }

    // not using new Date(dateString) as it handles the components in UTC/GMT time zone.
    // splitting the components and creating a new Date directly handles them it local time zone.
    const split = dateString.split('-');
    const [year, month, day] = split.map((x) => parseInt(x, 10));
    return new Date(year, month - 1, day);
}

function parseWinTaxBounds(taxLimits: string): WinTaxBound[] {
    if (!taxLimits || taxLimits === defaultTaxLevel) {
        return [];
    }
    const limits = taxLimits.split('|').map(parseFloat);
    // handle odd limits length. Should never happen
    const maxLength = (limits.length % 2) ? limits.length - 1 : limits.length;
    if (limits.length % 2) {
        logger.error(new Error('Tax limits have invalid format'));
    }
    const limitsList: WinTaxBound[] = [];
    for (let i = 0; i < maxLength - 1; i += 2) {
        limitsList.push({
            lowerBound: limits[i],
            percentage: limits[i + 1],
        });
    }
    return limitsList;
}
